import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Check, ArrowRight, FileText, MessageSquare, Download, Clock, Star, ClipboardCheck, Search, DollarSign, Briefcase, Send, RefreshCw, UserCheck, Shield } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cb900e0f-c443-41f6-9df3-17a2db93c215/artifacts/qd501ira_D70BB6EE-7344-46BE-86BC-6C6D197A54F6.png";

const services = [
  {
    id: 'resume_refresh',
    name: 'Resume Refresh',
    startingPrice: 59,
    description: 'Light cleanup for resumes that already have a decent base but need stronger formatting, wording, and polish.',
    features: ['ATS-friendly formatting cleanup', 'Wording and grammar polish', 'Cleaner layout and readability', '1 revision round'],
  },
  {
    id: 'resume_rewrite',
    name: 'Resume Rewrite',
    startingPrice: 99,
    description: 'A stronger rewrite for job seekers who need better positioning, sharper bullet points, and a cleaner final resume.',
    features: ['Full resume rewrite', 'ATS-friendly single-column layout', 'Achievement-focused bullet improvement', 'Keyword alignment', '2 revision rounds'],
    highlighted: true,
  },
  {
    id: 'job_search_bundle',
    name: 'Job Search Bundle',
    startingPrice: 149,
    description: 'A bundled option for job seekers who want their core materials aligned and ready to use.',
    features: ['Resume rewrite', 'Cover letter draft', 'LinkedIn headline + About rewrite', '2 revision rounds'],
    bestValue: true,
  },
  {
    id: 'application_support',
    name: 'Application Support',
    startingPrice: 99,
    description: 'Hands-on help for job seekers who want structured support with applications.',
    features: ['Up to 10 applications', 'Client approves target roles', 'Organized support window', 'Progress tracking'],
  }
];

const testimonials = [
  { name: 'Sarah M.', text: 'Got 3 interviews within a week of using my new resume. The personal review made all the difference.' },
  { name: 'James L.', text: 'I appreciated that they reviewed my materials before charging me. The final result exceeded my expectations.' },
  { name: 'Michelle T.', text: 'Finally got callbacks after months of silence. The scope confirmation helped me understand exactly what I was getting.' },
  { name: 'David K.', text: 'Clear process, clean communication, and a resume that actually gets results.' },
  { name: 'Lisa R.', text: 'The revision process was smooth. They refined it until I was completely satisfied.' },
  { name: 'Marcus J.', text: 'Took the time to understand my goals before starting. The final deliverable was polished and professional.' },
  { name: 'Emily C.', text: 'Switched careers successfully. The personalized approach made the transition so much easier.' },
  { name: 'Robert S.', text: 'Been in HR for 10 years. This is exactly what we look for in resumes.' }
];

const faqs = [
  { q: 'How does the process work?', a: 'You start by submitting your intake and uploading your materials. We review everything first, confirm the right scope and pricing, and only then send an invoice. Work begins after payment is received.' },
  { q: 'Do I pay right away?', a: 'No. Every project is reviewed before billing. We confirm the right service and price first, then send your invoice.' },
  { q: 'Are revisions included?', a: 'Yes. Reasonable revisions are included within the agreed scope of the project.' },
  { q: 'How long does turnaround take?', a: 'Turnaround depends on the service and complexity of the project, but exact timing is confirmed after intake and before billing.' },
  { q: 'Do you use AI?', a: 'We use modern tools to help with drafting, formatting, and workflow efficiency, but every final deliverable is personally reviewed and refined before it is sent.' },
  { q: 'Can I get LinkedIn or cover letter help too?', a: 'Yes. LinkedIn support and cover letter help can be added as standalone services or included in a bundle, depending on your needs.' },
];

export default function HomePage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-hero min-h-screen relative overflow-hidden">
        <Header variant="dark" />
        
        <div className="max-w-7xl mx-auto px-6 pt-32 pb-20 md:pt-40 md:pb-32">
          <div className="text-center max-w-4xl mx-auto">
            <Badge className="bg-white/10 text-white border-white/20 mb-6">
              Every Project Personally Reviewed &bull; No Charge Until Scope Confirmed
            </Badge>
            
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white mb-6 tracking-tight leading-[1.1]" style={{ fontFamily: 'Outfit' }}>
              Your Resume, <br />
              <span className="text-teal-300">Professionally Refined</span>
            </h1>
            
            <p className="text-lg md:text-xl text-white/80 mb-10 max-w-2xl mx-auto leading-relaxed">
              ATS-friendly resume help with personal review on every project. Submit your materials first, and we'll confirm the right scope and pricing before any invoice is sent.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => navigate('/start')}
                className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-8 py-6 text-lg font-semibold shadow-lg btn-glow"
                data-testid="hero-start-btn"
              >
                Start Your Intake
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                onClick={() => navigate('/pricing')}
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg"
                data-testid="hero-pricing-btn"
              >
                View Services
              </Button>
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-slate-50 to-transparent"></div>
      </section>

      {/* Trust Strip */}
      <section className="bg-slate-50 py-8 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-8 md:gap-16 text-slate-600">
            <div className="flex items-center gap-2">
              <UserCheck className="h-5 w-5 text-teal-600" />
              <span className="font-medium">Personally Reviewed</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="h-5 w-5 text-teal-600" />
              <span className="font-medium">Scope Confirmed First</span>
            </div>
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-teal-600" />
              <span className="font-medium">ATS-Friendly Formatting</span>
            </div>
            <div className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-teal-600" />
              <span className="font-medium">Revisions Included</span>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-slate-50 py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit' }}>
              How It Works
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              A simple, transparent process. You're never billed until we review your materials and confirm the right scope.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {[
              { icon: ClipboardCheck, title: 'Submit Your Intake', desc: 'Upload your current resume and tell us what kind of help you need.' },
              { icon: Search, title: 'We Review Your Materials', desc: 'Every project is reviewed personally before scope is confirmed.' },
              { icon: MessageSquare, title: 'Scope Confirmed', desc: 'We recommend the right service, turnaround, and pricing based on your needs.' },
              { icon: DollarSign, title: 'Invoice Sent', desc: 'Once approved, your invoice is sent and your project is scheduled.' },
            ].map((step, i) => (
              <div key={i} className="text-center">
                <div className="w-16 h-16 bg-teal-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <step.icon className="h-8 w-8 text-teal-700" />
                </div>
                <div className="text-sm font-semibold text-teal-700 mb-2">Step {i + 1}</div>
                <h3 className="font-semibold text-slate-900 mb-2">{step.title}</h3>
                <p className="text-slate-600 text-sm">{step.desc}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Briefcase, title: 'Work Begins', desc: 'After payment is received, we begin drafting and refining your materials.' },
              { icon: Send, title: 'Final Delivery', desc: 'Your polished, ATS-friendly documents are delivered.' },
              { icon: RefreshCw, title: 'Revisions Within Scope', desc: 'Reasonable revisions are included within the agreed scope.' },
            ].map((step, i) => (
              <div key={i} className="text-center">
                <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <step.icon className="h-8 w-8 text-slate-700" />
                </div>
                <div className="text-sm font-semibold text-slate-500 mb-2">Step {i + 5}</div>
                <h3 className="font-semibold text-slate-900 mb-2">{step.title}</h3>
                <p className="text-slate-600 text-sm">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="bg-white py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit' }}>
              Services
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Every project starts with intake so we can review your materials and confirm the right scope before invoicing.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {services.map((service) => (
              <Card 
                key={service.id} 
                className={`relative p-6 rounded-2xl border-2 transition-all card-hover ${
                  service.bestValue ? 'border-teal-500 shadow-xl bg-teal-50/30' :
                  service.id === 'application_support' ? 'border-amber-300 bg-amber-50/30' :
                  'border-slate-200'
                }`}
                data-testid={`service-card-${service.id}`}
              >
                {service.bestValue && (
                  <div className="absolute top-3 right-3">
                    <Badge className="bg-teal-600 text-white text-xs">Best Value</Badge>
                  </div>
                )}

                <div className="logo-badge" style={{ top: '-20px', width: '44px', height: '44px' }}>
                  <img src={LOGO_URL} alt="" className="h-6 w-6 rounded-lg" />
                </div>

                <div className="pt-4">
                  <h3 className="text-lg font-bold text-slate-900 mb-1" style={{ fontFamily: 'Outfit' }}>{service.name}</h3>
                  <div className="flex items-baseline gap-1 mb-2">
                    <span className="text-sm text-slate-500">Starting at</span>
                    <span className="text-2xl font-bold text-slate-900">${service.startingPrice}</span>
                  </div>
                  <p className="text-sm text-slate-600 mb-4">{service.description}</p>

                  <ul className="space-y-2 mb-6">
                    {service.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                        <Check className={`h-4 w-4 flex-shrink-0 mt-0.5 ${service.id === 'application_support' ? 'text-amber-600' : 'text-teal-600'}`} />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </Card>
            ))}
          </div>

          <p className="text-center text-slate-500 text-sm mb-8">
            Additional LinkedIn and cover letter support can also be added after intake if needed.
          </p>

          {/* Trust Message */}
          <Card className="max-w-3xl mx-auto p-6 rounded-2xl bg-teal-50 border-teal-200">
            <div className="text-center">
              <UserCheck className="h-10 w-10 text-teal-600 mx-auto mb-3" />
              <h3 className="font-semibold text-slate-900 mb-2">Every project includes reasonable revisions within the agreed scope</h3>
              <p className="text-slate-600 text-sm">
                We work with you to refine the final result so it feels ready to use.
              </p>
            </div>
          </Card>

          <div className="text-center mt-8">
            <Button 
              onClick={() => navigate('/start')}
              className="bg-teal-600 hover:bg-teal-700 rounded-full px-8 py-6 text-lg font-semibold"
              data-testid="services-start-btn"
            >
              Start Your Intake
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-slate-50 py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit' }}>
              What Clients Say
            </h2>
            <p className="text-slate-600 text-lg">Real feedback from real projects</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {testimonials.map((t, i) => (
              <Card key={i} className="p-6 rounded-2xl bg-white border border-slate-100" data-testid={`testimonial-${i}`}>
                <div className="flex gap-1 mb-3">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className="h-4 w-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="text-slate-700 text-sm mb-4">"{t.text}"</p>
                <p className="text-slate-900 font-semibold text-sm">&mdash; {t.name}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Before & After Transformations */}
      <section className="bg-white py-24" data-testid="before-after-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit' }}>
              Before & After
            </h2>
            <p className="text-slate-600 text-lg">See what a professionally refined resume looks like</p>
          </div>

          <div className="space-y-16">
            {/* Sample 1 - Jasmine Carter */}
            <Card className="p-6 md:p-8 rounded-3xl border border-slate-200 overflow-hidden" data-testid="transformation-1">
              <div className="mb-8 text-center">
                <Badge className="bg-pink-100 text-pink-700 mb-3">Marketing & Communications</Badge>
                <h3 className="text-2xl font-bold text-slate-900 mt-2">Jasmine Carter</h3>
                <p className="text-slate-600 mt-2 max-w-2xl mx-auto">
                  Transformed a basic marketing coordinator resume into a compelling narrative showcasing campaign results, ROI metrics, and leadership potential.
                </p>
              </div>
              <div className="grid md:grid-cols-2 gap-8">
                <a 
                  href="https://customer-assets.emergentagent.com/job_resumesurgeon/artifacts/e3k454lx_ResumeSurgeon_Sample1_JasmineCarter_BEFORE_v2.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block"
                >
                  <div className="relative">
                    <div className="absolute -top-3 left-4 z-10">
                      <span className="px-4 py-1.5 bg-red-500 text-white text-sm font-bold rounded-full shadow-lg">BEFORE</span>
                    </div>
                    <div className="bg-red-50 rounded-2xl p-3 pt-6 border-2 border-red-200 hover:border-red-400 hover:shadow-lg transition-all">
                      <img src="/samples/jasmine_before.png" alt="Jasmine Carter Resume - Before" className="w-full rounded-lg shadow-inner" />
                      <div className="mt-3 text-center">
                        <span className="text-xs text-red-500 flex items-center justify-center gap-1 group-hover:text-red-600">
                          <FileText className="h-3 w-3" /> Click to view full PDF
                        </span>
                      </div>
                    </div>
                  </div>
                </a>
                <a 
                  href="https://customer-assets.emergentagent.com/job_resumesurgeon/artifacts/dn4n15lu_ResumeSurgeon_Sample1_JasmineCarter_FINAL.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block"
                >
                  <div className="relative">
                    <div className="absolute -top-3 left-4 z-10">
                      <span className="px-4 py-1.5 bg-teal-500 text-white text-sm font-bold rounded-full shadow-lg">AFTER</span>
                    </div>
                    <div className="bg-teal-50 rounded-2xl p-3 pt-6 border-2 border-teal-200 hover:border-teal-400 hover:shadow-lg transition-all">
                      <img src="/samples/jasmine_after.png" alt="Jasmine Carter Resume - After" className="w-full rounded-lg shadow-inner" />
                      <div className="mt-3 text-center">
                        <span className="text-xs text-teal-500 flex items-center justify-center gap-1 group-hover:text-teal-600">
                          <FileText className="h-3 w-3" /> Click to view full PDF
                        </span>
                      </div>
                    </div>
                  </div>
                </a>
              </div>
            </Card>

            {/* Sample 2 - Miguel Reyes */}
            <Card className="p-6 md:p-8 rounded-3xl border border-slate-200 overflow-hidden" data-testid="transformation-2">
              <div className="mb-8 text-center">
                <Badge className="bg-blue-100 text-blue-700 mb-3">Sales & Business Development</Badge>
                <h3 className="text-2xl font-bold text-slate-900 mt-2">Miguel Reyes</h3>
                <p className="text-slate-600 mt-2 max-w-2xl mx-auto">
                  Transformed a generic sales resume into a metrics-driven document highlighting revenue achievements and strategic account management.
                </p>
              </div>
              <div className="grid md:grid-cols-2 gap-8">
                <a href="https://customer-assets.emergentagent.com/job_resumesurgeon/artifacts/zl7ysgo1_ResumeSurgeon_Sample2_MiguelReyes_BEFORE_v2.pdf" target="_blank" rel="noopener noreferrer" className="group block">
                  <div className="relative">
                    <div className="absolute -top-3 left-4 z-10"><span className="px-4 py-1.5 bg-red-500 text-white text-sm font-bold rounded-full shadow-lg">BEFORE</span></div>
                    <div className="bg-red-50 rounded-2xl p-3 pt-6 border-2 border-red-200 hover:border-red-400 hover:shadow-lg transition-all">
                      <img src="/samples/miguel_before.png" alt="Miguel Reyes Resume - Before" className="w-full rounded-lg shadow-inner" />
                      <div className="mt-3 text-center"><span className="text-xs text-red-500 flex items-center justify-center gap-1 group-hover:text-red-600"><FileText className="h-3 w-3" /> Click to view full PDF</span></div>
                    </div>
                  </div>
                </a>
                <a href="https://customer-assets.emergentagent.com/job_resumesurgeon/artifacts/tvoh88ub_ResumeSurgeon_Sample2_MiguelReyes_FINAL.pdf" target="_blank" rel="noopener noreferrer" className="group block">
                  <div className="relative">
                    <div className="absolute -top-3 left-4 z-10"><span className="px-4 py-1.5 bg-teal-500 text-white text-sm font-bold rounded-full shadow-lg">AFTER</span></div>
                    <div className="bg-teal-50 rounded-2xl p-3 pt-6 border-2 border-teal-200 hover:border-teal-400 hover:shadow-lg transition-all">
                      <img src="/samples/miguel_after.png" alt="Miguel Reyes Resume - After" className="w-full rounded-lg shadow-inner" />
                      <div className="mt-3 text-center"><span className="text-xs text-teal-500 flex items-center justify-center gap-1 group-hover:text-teal-600"><FileText className="h-3 w-3" /> Click to view full PDF</span></div>
                    </div>
                  </div>
                </a>
              </div>
            </Card>
          </div>

          <div className="text-center mt-12">
            <Button onClick={() => navigate('/samples')} variant="outline" className="rounded-full px-8">
              View More Samples
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-slate-50 py-24">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit' }}>
              Frequently Asked Questions
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border border-slate-200 rounded-xl px-6 bg-white" data-testid={`faq-${i}`}>
                <AccordionTrigger className="text-left font-semibold text-slate-900 hover:no-underline py-4">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 pb-4">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <div className="text-center mt-8">
            <Button variant="link" onClick={() => navigate('/faq')} className="text-teal-700">
              View all FAQs <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-gradient-hero py-24">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit' }}>
            Ready to Get Started?
          </h2>
          <p className="text-white/80 text-lg mb-8">
            Submit your intake and we'll review your materials personally before confirming the right scope and pricing.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => navigate('/start')}
              className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-8 py-6 text-lg font-semibold"
              data-testid="final-cta-start"
            >
              Start Your Intake
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              onClick={() => navigate('/pricing')}
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg"
              data-testid="final-cta-pricing"
            >
              View Services & Pricing
            </Button>
          </div>
        </div>
      </section>

      <Footer />

      {/* Sticky Mobile CTA */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-slate-200 md:hidden z-40">
        <Button 
          onClick={() => navigate('/start')}
          className="w-full bg-teal-600 hover:bg-teal-700 rounded-full py-6 text-lg font-semibold"
          data-testid="sticky-cta"
        >
          Start Your Intake
        </Button>
      </div>
    </div>
  );
}
